import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ProjectSubHeader from "../components/ProjectSubHeader";
import { MultiSelect } from "primereact/multiselect";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import { formatName, formatSubcontractorName } from "../../common/Misc";
import { API, get } from "../../../config";
import { useParams } from "react-router-dom";
import { Button } from "react-bootstrap";
import ProgressPayment from "./ProgressPayment";
import PageNotFound from "../../PageNotFound";

const ProgressPaymentContract = () => {
  const roleType = localStorage.getItem("roleType");

  const params = useParams();
  const projectId = params?.project_id;
  const [globalContractSelect, setGlobalContractSelect] = useState([]);

  const userId = localStorage.getItem("userId");

  const [selectedContracts, setSelectedContracts] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [selectedMainContracts, setSelectedMainContracts] = useState([]);
  const [contractOptions, setContractOptions] = useState([]);
  const [contractCurrentPage, setContractCurrentPage] = useState(1);
  const [contractHasMoreData, setContractHasMoreData] = useState(true);
  const [contractListLoading, setContractListLoading] = useState(false);

  const filterContractInputRef = useRef();

  const handleContractChange = (e) => {
    setSelectedContracts(e.value);
  };

  const handleContractApplyFilter = () => {
    setSelectedMainContracts(selectedContracts);
    setGlobalContractSelect && setGlobalContractSelect(selectedContracts);
    filterContractInputRef.current.hide();
  };

  const myContractResetFunction = () => {
    setSelectedContracts([]);
    setSelectedMainContracts([]);
    setGlobalContractSelect && setGlobalContractSelect([]);
    filterContractInputRef && filterContractInputRef.current.focus();
  };

  const handleContractList = useCallback(
    async (page) => {
      if (page !== 1 && (contractListLoading || !contractHasMoreData)) return;
      if (page === 1) {
        setContractOptions([]);
      }
      try {
        setContractListLoading(true);
        let response = await get(
          `${API.GET_CONTRACTS}?contact_user_id=${userId}&builder_project_id=${projectId}&limit=25&page=${page}`
        );
        if (response?.data?.status === 200) {
          const list =
            response &&
            response?.data?.data?.data?.length > 0 &&
            response?.data?.data?.data?.map((item) => {
              const list = {
                label: item?.contract_number,
                value: item?.id,
                ...item,
              };
              return list;
            });
          // setContractOptions(list ? list : []);
          // setLoading(false);
          let contractItemList = list ? list : [];
          setContractOptions((prev) => [...prev, ...contractItemList]);
          list?.length > 0 && setSelectedContracts([list[0]?.value]);
          list?.length > 0 && setSelectedMainContracts([list[0]?.value]);
          list?.length > 0 && setGlobalContractSelect([list[0]?.value]);
          setContractCurrentPage(response?.data?.data?.current_page);
          // Update `hasMoreData` when the last page is reached
          if (
            response?.data?.data?.current_page >=
              response?.data?.data?.last_page ||
            response?.data?.data?.data.length === 0
          ) {
            setContractHasMoreData(false);
          } else {
            setContractHasMoreData(true);
          }
          setContractListLoading(false);
        }
      } catch (error) {
        setContractOptions([]);
        setContractListLoading(false);
        if (error?.response?.data?.errors) {
          Object.keys(error?.response?.data?.errors).forEach((key) => {
            toast.error(error?.response?.data?.errors[key][0]);
          });
        } else {
          toast.error(error?.message);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId]
  );

  useEffect(() => {
    handleContractList(1);
  }, [handleContractList]);

  const contractLoadMoreData = useCallback(
    (event) => {
      const { last } = event;

      // Trigger API call if scrolled near the end of the loaded data
      if (
        !contractListLoading &&
        contractHasMoreData &&
        last >= contractOptions.length
      ) {
        handleContractList(contractCurrentPage + 1);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contractListLoading, contractHasMoreData, contractOptions.length]
  );

  const fetchContractSearchData = async (query) => {
    if (query) {
      try {
        setContractListLoading(true);
        const response = await get(
          `${API.GET_CONTACTS}?builder_project_id=${
            projectId ? projectId : ""
          }&search=${
            query ? query : ""
          }&with_pta=1&limit=25&sort_column=name&sort_order=ASC&page=1`
        );
        if (response?.data?.status === 200) {
          const list =
            response &&
            response?.data?.data?.data?.length > 0 &&
            response?.data?.data?.data?.map((item) => {
              const list = {
                label:
                  item?.is_pta === 0
                    ? `${formatName(item?.first_name, item?.last_name)} (RTA)`
                    : item?.is_pta === 1
                    ? `${formatName(item?.first_name, item?.last_name)} (PTA)`
                    : item?.is_pta === 2
                    ? `${formatName(
                        item?.first_name,
                        item?.last_name
                      )} (Withdrawal)`
                    : item?.is_pta === 3
                    ? `${formatName(
                        item?.first_name,
                        item?.last_name
                      )} (Principal)`
                    : item?.is_pta === 4
                    ? `${formatName(
                        item?.first_name,
                        item?.last_name
                      )} (Trustee)`
                    : formatSubcontractorName(item),
                value: item?.id,
              };
              return list;
            });
          setContractOptions(list);
          setContractCurrentPage(response?.data?.data?.current_page);
          // Update `hasMoreData` when the last page is reached
          if (
            response?.data?.data?.current_page >=
              response?.data?.data?.last_page ||
            response?.data?.data?.data.length === 0
          ) {
            setContractHasMoreData(false);
          } else {
            setContractHasMoreData(true);
          }
          setContractListLoading(false);
        }
      } catch (error) {
        setContractListLoading(false);
        if (error?.response?.data?.errors) {
          Object.keys(error?.response?.data?.errors).forEach((key) => {
            toast.error(error?.response?.data?.errors[key][0]);
          });
        } else {
          toast.error(error?.message);
        }
      }
    }
  };

  const debouncedContractFetchData = useMemo(
    () => debounce(fetchContractSearchData, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Handle filtering
  const handleContractSearch = (event) => {
    const query = event.filter;
    debouncedContractFetchData(query); // Call debounced function
  };

  // Clean up the debounced function on unmount
  useMemo(
    () => () => debouncedContractFetchData.cancel(),
    [debouncedContractFetchData]
  );

  const handleContractFilter = async (e) => {
    if (e?.filter) {
      if (contractOptions?.find((item) => item?.label?.includes(e?.filter))) {
      } else {
        await handleContractSearch(e);
      }
    } else {
      await handleContractList(1);
    }
  };

  return roleType !== "Contact" ? (
    <PageNotFound />
  ) : (
    <div className="cms-page doc-list-page">
      <div className="page-content-block">
        <div className="full-content-block">
          <ProjectSubHeader title={"Documents"} />
          <div className="content-details">
            <div className="table-top-btn">
              <div className="filter-selections">
                <MultiSelect
                  value={selectedContracts}
                  onChange={(e) => {
                    handleContractChange(e);
                  }}
                  options={
                    contractOptions?.length > 0
                      ? contractOptions?.filter(
                          (option, index, self) =>
                            index ===
                            self.findIndex((o) => o.value === option.value)
                        )
                      : []
                  }
                  showSelectAll={false}
                  optionLabel="label"
                  filter
                  placeholder="Select Contract(s)"
                  maxSelectedLabels={1}
                  selectedItemTemplate={(e) =>
                    selectedContracts?.length === 1 &&
                    contractOptions?.find((item) => item.value === e)?.label
                  }
                  selectedItemsLabel={`${selectedContracts?.length} Contract(s) Selected`}
                  onHide={() => {
                    setSelectedMainContracts(selectedContracts);
                    setGlobalContractSelect &&
                      setGlobalContractSelect(selectedContracts);
                  }}
                  style={{
                    fontSize: "13px",
                    maxWidth: "350px",
                  }}
                  loading={contractListLoading}
                  onFilter={(e) => handleContractFilter(e)}
                  virtualScrollerOptions={{
                    lazy: true, // Enables lazy loading
                    onLazyLoad: contractLoadMoreData, // Triggered on scroll
                    itemSize: 40, // Height of each item
                  }}
                  ref={filterContractInputRef}
                  panelFooterTemplate={
                    <>
                      <Button
                        className="multiSelect-button"
                        onClick={() => {
                          handleContractApplyFilter();
                        }}
                      >
                        Apply
                      </Button>
                      <Button
                        className="multiSelect-button"
                        onClick={() => {
                          myContractResetFunction();
                        }}
                      >
                        Reset
                      </Button>
                    </>
                  }
                />
              </div>
            </div>

            <div className="custom-table tab-table">
              <ProgressPayment
                contractOptions={contractOptions}
                globalContractSelect={globalContractSelect}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressPaymentContract;
